/**
 * What the customer's quote page counts for the choices made (supplier interface spec 53 §3, ruling 47 as widened on
 * 27 Oct 2026). A line of several quantities has a delivery for each (`for_quantity_option_position`, the item it
 * carries `delivers_line_no`), but only the delivery of the quantity the customer chooses is part of what they pay, so
 * the total and the price list count it and leave the others out. The backend's open total counts the first quantity's
 * (the quote's rule); once accepted the quote holds only the chosen quantity's delivery, so nothing is left out.
 */
import { formatQuotedPrice } from "./formatters";

/** How the page is being read: choosing quantities, listing an open quote, or showing a decided one. */
export type QuoteChoiceMode = "choosing" | "listing" | "decided";

/** The customer's choices so far: line number to the chosen option's position, and the ticked extras. */
export interface QuoteChoiceState {
  readonly chosen: Readonly<Record<number, number>>;
  readonly extras: ReadonlySet<number>;
}

/** The part of a customer quote line that the choice rules read. */
export interface QuoteChoiceLine {
  line_no: number;
  is_optional: boolean;
  /** Ex VAT, at the line's first quantity option; null on a delivery not yet priced. */
  price_pence: number | null;
  /** A delivery of one quantity of an item line offering several: that quantity's position. */
  for_quantity_option_position?: number | null;
  /** With `for_quantity_option_position`: the line number of the item it carries. */
  delivers_line_no?: number | null;
  quantity_options: ReadonlyArray<{ position: number; price_pence: number }>;
}

/**
 * A delivery that names a quantity counts only for that quantity: the one chosen while choosing, the first while an
 * open quote is only listed, and any that is there once decided (the quote then holds the chosen one alone).
 */
export function isDeliveryCounted(
  line: Pick<
    QuoteChoiceLine,
    "for_quantity_option_position" | "delivers_line_no"
  >,
  state: Pick<QuoteChoiceState, "chosen">,
  mode: QuoteChoiceMode,
): boolean {
  const position = line.for_quantity_option_position;
  if (position === null || position === undefined || mode === "decided")
    return true;
  if (mode === "listing") return position === 1;
  return (
    line.delivers_line_no !== null &&
    line.delivers_line_no !== undefined &&
    state.chosen[line.delivers_line_no] === position
  );
}

/** The lines the choice counts: an optional extra only when ticked, a delivery only for its own quantity. */
export function linesCounted<L extends QuoteChoiceLine>(
  lines: readonly L[],
  state: QuoteChoiceState,
  mode: QuoteChoiceMode,
): L[] {
  return lines.filter(
    (line) =>
      (mode !== "choosing" ||
        !line.is_optional ||
        state.extras.has(line.line_no)) &&
      isDeliveryCounted(line, state, mode),
  );
}

/** What one counted line adds, ex VAT: its chosen option's price while choosing, else its own. */
export function countedPricePence(
  line: QuoteChoiceLine,
  state: Pick<QuoteChoiceState, "chosen">,
  mode: QuoteChoiceMode,
): number {
  if (mode !== "choosing" || line.quantity_options.length === 0)
    return line.price_pence ?? 0;
  return (
    line.quantity_options.find((o) => o.position === state.chosen[line.line_no])
      ?.price_pence ?? 0
  );
}

/** The total of the choices made, ex VAT: a delivery not yet priced adds nothing until staff agree it. */
export function choiceTotalPence(
  lines: readonly QuoteChoiceLine[],
  state: QuoteChoiceState,
  mode: QuoteChoiceMode,
): number {
  return linesCounted(lines, state, mode).reduce(
    (sum, line) => sum + countedPricePence(line, state, mode),
    0,
  );
}

/** One quantity's delivery as the backend reads it (`CustomerQuoteOption`). */
export interface OptionDeliveryFacts {
  delivery_status?: "priced" | "included" | "not_yet_priced" | null;
  delivery_pence?: number | null;
}

/** "delivery £12.00", "delivery included" or "delivery not yet priced"; null when the line has one delivery for all its quantities. */
export function optionDeliveryNote(option: OptionDeliveryFacts): string | null {
  switch (option.delivery_status) {
    case "priced":
      return `delivery ${formatQuotedPrice(option.delivery_pence ?? 0)}`;
    case "included":
      return "delivery included";
    case "not_yet_priced":
      return "delivery not yet priced";
    default:
      return null;
  }
}
